import { useSearch } from "@tanstack/react-router";
import { useMe } from "./use-me";

/**
 * "The mailbox I'm using" — the mailbox switcher (Phase 5) added a
 * `mailbox` URL search param (like `folder`/`q`, see router.tsx) that picks
 * one of `/api/me`'s mailboxes by id, or the sentinel "all" for the
 * cross-mailbox aggregate view. Falls back to the first mailbox (stable,
 * alphabetical by display name per lib/d1.ts's ORDER BY) when unset or
 * pointing at a mailbox this user no longer has access to — this is what
 * let every pre-Phase-5 call site (which just reads `mailboxId`) keep
 * working untouched for the single-mailbox case.
 */
export function useMailbox() {
  const me = useMe();
  // strict: false because this hook is called from components mounted at
  // different route depths (mail-shell.tsx sits at the pathless
  // "mail-layout" route that owns `mailbox`; thread-detail-route.tsx is a
  // child of it) — see the same pattern/rationale in mail-shell.tsx's
  // useSearch call.
  const search = useSearch({ strict: false }) as { mailbox?: string };

  const mailboxes = me.data?.mailboxes ?? [];
  const requestedId = search.mailbox;
  const isAggregate = requestedId === "all" && mailboxes.length > 1;
  const selected = !isAggregate && requestedId ? mailboxes.find((m) => m.id === requestedId) : undefined;
  const current = selected ?? mailboxes[0];

  const mailboxId = isAggregate ? undefined : current?.id;
  const selfEmail = me.data?.user.email;
  const isOwnerOfAny = mailboxes.some((m) => m.permissionLevel === "owner");

  return { ...me, mailboxId, selfEmail, mailboxes, currentMailbox: current, isAggregate, isOwnerOfAny };
}
